import { convertHexToRgba, filterClasses } from "@swivl-mono/helpers";
import { CSSProperties, type ComponentPropsWithoutRef, ReactNode, forwardRef, useEffect, useState } from "react";
import { CgSpinner } from "react-icons/cg";

import "./Button.css";

export enum ButtonVariant {
  PRIMARY = "primary",
  GHOST = "ghost",
  DASHED = "dashed",
  ICON = "icon",
  FLOAT = "float",
  /**
   * Inline text action — no padding, no min-width, no height box, so it sits on
   * the text baseline beside the content it acts on. Use for actions that read
   * as part of a row ("Add window", "Mark closed"); use GHOST when the action
   * needs a button-sized hit area alongside other buttons.
   */
  TEXT = "text"
}

export enum ButtonSize {
  SMALL = "small",
  MEDIUM = "medium",
  LARGE = "large"
}

export enum ButtonColor {
  ORANGE = "orange",
  YELLOW = "yellow",
  RED = "red",
  TEAL = "teal",
  BLUE = "blue",
  BLACK = "black",
  WHITE = "white",
  DARK_GREY = "darkGrey",
  LIGHT_GREY = "lightGrey",
  TRANSPARENT = "transparent",
  THEMED = "themed"
}

enum Outlet {
  CONTAINER = "container",
  CONTENT = "content",
  LEFT_ICON = "leftIcon",
  SPINNER = "spinner"
}

interface ButtonProps extends Omit<ComponentPropsWithoutRef<"button">, "className" | "disabled"> {
  children: ReactNode;
  className?: string;
  classNames?: { [Key in Outlet]?: string };
  disabled?: boolean;
  leftIcon?: ReactNode;
  loading?: boolean;
  onClickAsync?: () => Promise<void>;
  color?: ButtonColor;
  size?: ButtonSize;
  variant?: ButtonVariant;
  fullWidth?: boolean;
  type?: "button" | "submit" | "reset";
}

// colors for button background / gradient and text
const buttonColorMap: Record<ButtonColor, { main: string; accent?: string; text: string }> = {
  [ButtonColor.ORANGE]: { accent: "#ff7d33", main: "#ff5b02", text: "#ffffff" },
  [ButtonColor.TEAL]: { accent: "#01cbc0", main: "#00aaa2", text: "#ffffff" },
  [ButtonColor.WHITE]: { accent: "#ffffff", main: "#f5f5f5", text: "#000000" },
  [ButtonColor.YELLOW]: { accent: "#ffd41e", main: "#ffb801", text: "#000000" },
  [ButtonColor.BLACK]: { accent: "#323232", main: "#1b1b1b", text: "#ffffff" },
  [ButtonColor.TRANSPARENT]: { main: "transparent", text: "#000000" },
  [ButtonColor.LIGHT_GREY]: { main: "#d9d9d9", accent: "#bfbfbf", text: "#000000" },
  [ButtonColor.BLUE]: { main: "#007bff", accent: "#0056b3", text: "#ffffff" },
  [ButtonColor.DARK_GREY]: { main: "#707070", accent: "#4b4b4b", text: "#ffffff" },
  [ButtonColor.RED]: { main: "#e8382f", accent: "#ff4b34", text: "#ffffff" },
  [ButtonColor.THEMED]: {
    main: "color-mix(in srgb, var(--swivl-primaryBackground) 70%, black)",
    accent: "var(--swivl-primaryBackground)",
    text: "var(--swivl-primaryText)"
  }
};

const getButtonColor = (color: ButtonColor, disabled?: boolean) => {
  if (disabled) {
    return {
      "--btn-main": "var(--color-grey-400)",
      "--btn-accent": "var(--color-grey-400)",
      "--btn-text": "var(--color-grey-500)",
      "--btn-bg": `var(--color-grey-400), var(--color-grey-400)`
    };
  }

  const { main, accent, text } = buttonColorMap[color];
  const textTap = text.startsWith("var(") ? `color-mix(in srgb, ${text} 70%, transparent)` : convertHexToRgba(text, 0.7);
  return {
    "--btn-main": main,
    "--btn-accent": accent ?? main,
    "--btn-text": text,
    "--btn-bg": `${accent ?? main}, ${main}, ${accent ?? main}`,
    "--btn-text-tap": textTap
  };
};

const BaseButton = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      classNames,
      color = ButtonColor.BLACK,
      disabled = false,
      leftIcon = null,
      loading = false,
      onClick,
      onClickAsync,
      size = ButtonSize.MEDIUM,
      variant = ButtonVariant.PRIMARY,
      fullWidth = false,
      type = "button",
      ...rest
    },
    ref
  ) => {
    const [isLoading, setIsLoading] = useState(loading ? true : false);
    useEffect(() => {
      setIsLoading(loading ? true : false);
    }, [loading]);

    // internal class names for sizes and variants
    const sharedSizeClasses: Record<ButtonSize, string> = {
      [ButtonSize.SMALL]: "px-3 h-[26px] min-w-[64px] text-sm",
      [ButtonSize.MEDIUM]: "px-3 h-8 min-w-[64px] text-sm",
      [ButtonSize.LARGE]: "px-3 h-10 min-w-[64px] text-base"
    };

    const buttonVariantClassNames: Record<
      ButtonVariant,
      {
        baseClasses: string;
        sizes?: Record<ButtonSize, string>;
      }
    > = {
      [ButtonVariant.PRIMARY]: {
        baseClasses: filterClasses(
          // button-primary (button.css) is handling shadows -- shadows are not animating in tw well -- handling with css vars in tw 4 should work ;)
          "button-primary rounded-sm border-none text-(--btn-text)",
          "bg-linear-to-b from-(--btn-accent) via-(--btn-main) to-(--btn-accent) bg-size-[200%_200%] bg-top",
          "hover:bg-bottom",
          "transition-all duration-300 ease-in-out",
          "active:fill-(--btn-text-tap) active:text-(--btn-text-tap)"
        ),
        sizes: sharedSizeClasses
      },
      [ButtonVariant.GHOST]: {
        baseClasses: filterClasses(
          `bg-transparent ${color === ButtonColor.WHITE ? "text-white" : "text-black"}`,
          "fill-orange-500 duration-200 hover:text-orange-500",
          "duration-100 active:fill-yellow-700 active:text-yellow-700",
          "transition-colors ease-in-out"
        ),
        sizes: sharedSizeClasses
      },
      [ButtonVariant.TEXT]: {
        baseClasses: filterClasses(
          `bg-transparent text-sm ${color === ButtonColor.WHITE ? "text-white" : "text-black"}`,
          "fill-current duration-200 hover:text-orange-500",
          "duration-100 active:text-yellow-700",
          "transition-colors ease-in-out"
        )
        // No `sizes` on purpose — that's what drops px-3, min-w and the fixed
        // height, leaving the label to size itself.
      },
      [ButtonVariant.DASHED]: {
        baseClasses: filterClasses(
          "rounded-sm border border-dashed bg-white text-black",
          "aria-expanded:border-orange-500 aria-expanded:bg-orange-200",
          "duration-200 hover:border-orange-500 hover:bg-orange-50 hover:text-orange-500",
          "duration-100 active:border-yellow-700 active:bg-yellow-50 active:fill-yellow-700 active:text-yellow-700",
          "transition-colors ease-in-out"
        ),
        sizes: sharedSizeClasses
      },
      [ButtonVariant.ICON]: {
        baseClasses: filterClasses(
          "button-primary rounded-sm border-none text-(--btn-text)",
          "bg-linear-to-b from-(--btn-accent) via-(--btn-main) to-(--btn-accent) bg-size-[200%_200%] bg-top",
          "hover:bg-bottom",
          "transition-all duration-300 ease-in-out",
          "active:fill-(--btn-text-tap) active:text-(--btn-text-tap) aria-expanded:bg-(--btn-accent) aria-expanded:fill-(--btn-text) aria-expanded:text-(--btn-text)"
        ),
        sizes: {
          [ButtonSize.SMALL]: "h-[26px] w-[30px]",
          [ButtonSize.MEDIUM]: "h-8 w-9",
          [ButtonSize.LARGE]: "h-10 w-11"
        }
      },
      [ButtonVariant.FLOAT]: {
        baseClasses: filterClasses(
          "button-float size-[26px] rounded-full bg-linear-to-b from-white to-grey-50 fill-orange-500 shadow-lg",
          "from-orange-500 to-[#ff7f3a] hover:scale-110 hover:bg-linear-to-b hover:fill-white",
          "transition-all duration-300 ease-in-out"
        )
      }
    };

    const handleClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (loading || disabled) {
        return;
      }

      if (onClickAsync) {
        try {
          setIsLoading(true);
          await onClickAsync();
        } catch (error) {
          console.error("Error occurred:", error);
        } finally {
          setIsLoading(false);
        }
      } else if (onClick) {
        onClick(e);
      }
    };

    const resolvedClassName = className || classNames?.container || "";

    return (
      <button
        ref={ref}
        style={getButtonColor(color, disabled) as CSSProperties}
        className={filterClasses(
          "relative flex items-center justify-center gap-1 fill-current font-semibold whitespace-nowrap",
          variant !== ButtonVariant.GHOST && variant !== ButtonVariant.TEXT && "disabled:bg-grey-300",
          "disabled:cursor-not-allowed disabled:fill-grey-500 disabled:text-grey-500",
          isLoading ? "pointer-events-none cursor-not-allowed opacity-80" : "pointer-events-auto cursor-pointer",
          fullWidth ? "w-full" : variant !== ButtonVariant.ICON && variant !== ButtonVariant.FLOAT ? "w-fit" : "",
          // variant classes
          buttonVariantClassNames[variant]?.baseClasses,
          // variant size classes
          (buttonVariantClassNames[variant]?.sizes?.[size] ?? buttonVariantClassNames[variant]?.sizes?.[ButtonSize.MEDIUM]) || "",
          resolvedClassName
        )}
        disabled={disabled}
        onClick={handleClick}
        {...rest}
      >
        {isLoading && (
          <span className={filterClasses(classNames?.spinner)}>
            <CgSpinner className="animate-spin fill-current" />
            <span className="sr-only">loading...</span>
          </span>
        )}
        {leftIcon && (
          <span className={filterClasses("stroke-current text-sm", classNames?.leftIcon, isLoading ? "invisible" : "visible")}>{leftIcon}</span>
        )}
        <div className={filterClasses("items-center gap-2", classNames?.content, isLoading && variant === ButtonVariant.ICON ? "hidden" : "flex")}>
          {children}
        </div>
      </button>
    );
  }
);

BaseButton.displayName = "Button";

export const Button = BaseButton;
